/**
 * A conferência da leitura do caso (E140, GUS-143, ADR-0096). Espelha
 * `apps/api/types/conferencia_da_leitura.py`.
 *
 * - `GET /casos/:id/conferencia-da-leitura`: a conferência mais recente, a última versão, a última
 *   versão da ficha e se a lida é vigente. Caso sem conferência é 404 `CONFERENCIA_NOT_FOUND` — ela
 *   nasce com o exame —, e o banco recebe 403: a conferência é da equipe do escritório.
 * - `POST /casos/:id/conferencia-da-leitura/pedidos`: a equipe pede para uma falha técnica "ler de
 *   novo" ou "registrar a limitação", e recebe a conferência refeita.
 *
 * A falha técnica (página não lida, campo essencial sem leitura) é da equipe e trava o documento
 * emitido; o ponto que pede juízo é do perito e não trava. Nada disso é pedido ao cliente.
 */
import axios, { type AxiosInstance } from 'axios';

import type { CampoDaFichaId, MotivoDaSituacao, PaginaNaFonte } from '@/lib/ficha_da_leitura';

export type CriterioId =
  | 'cobertura_das_paginas'
  | 'leitura_das_paginas'
  | 'campos_essenciais'
  | 'campos_no_papel'
  | 'divergencias_essenciais';

export type ResultadoDoCriterio = 'passou' | 'falhou' | 'inconclusivo' | 'nao_se_aplica';

export type EstadoDaConferencia = 'aprovada' | 'corrigir' | 'bloqueada';

export type Responsavel = 'operacao' | 'perito';

export type TipoDaPendencia =
  | 'pagina_omitida'
  | 'pagina_sem_documento'
  | 'pagina_por_ler'
  | 'pagina_com_falha'
  | 'pagina_sem_texto'
  | 'arquivo_sem_contagem'
  | 'documento_sem_leitura'
  | 'campo_essencial_sem_leitura'
  | 'campo_essencial_a_conferir'
  | 'campo_essencial_sem_trecho'
  | 'campo_no_papel_sem_uso'
  | 'divergencia_essencial';

export type TipoDoAchado = 'marcador_de_modelo' | 'campo_em_branco';

export type AcaoDaRetomada = 'tentativa' | 'limitacao';

export type EstadoDaLeituraDoCaso = 'pendente' | 'aprovada' | 'corrigir' | 'bloqueada';

export interface PendenciaDaLeituraData {
  chave: string;
  tipo: TipoDaPendencia;
  criterio: CriterioId;
  responsavel: Responsavel;
  retomavel: boolean;
  recuperavel: boolean;
  tentativas: number;
  limitacao: string | null;
  original_id: string | null;
  original_nome: string | null;
  pagina_no_original: number | null;
  documento_id: string | null;
  documento_nome: string | null;
  pagina: number | null;
  campo: CampoDaFichaId | null;
  referente_id: string | null;
  motivo_do_campo: MotivoDaSituacao | null;
}

export interface CriterioDaConferenciaData {
  criterio: CriterioId;
  resultado: ResultadoDoCriterio;
  pendencias: number;
  justificativa: string | null;
}

export interface AchadoDoDocumentoData {
  tipo: TipoDoAchado;
  documento_id: string;
  documento_nome: string;
  pagina: number | null;
  original_id: string;
  pagina_no_original: number | null;
  marca: string;
  trecho: string;
  campo: CampoDaFichaId | null;
}

export interface ConferenciaDaLeituraData {
  versao_da_regra: number;
  ficha_versao: number;
  ficha_sha256: string;
  estado: EstadoDaConferencia;
  criterios: CriterioDaConferenciaData[];
  pendencias: PendenciaDaLeituraData[];
  achados: AchadoDoDocumentoData[];
  paginas_no_inventario: number;
}

export interface ConferenciaDaLeituraRecordData {
  id: string;
  case_id: string;
  versao: number;
  sha256: string;
  criada_em: string;
  conferencia: ConferenciaDaLeituraData;
}

export interface ConferenciaDaLeituraOutData {
  conferencia: ConferenciaDaLeituraRecordData;
  ultima_versao: number;
  ficha_vigente: number;
  vigente: boolean;
}

export interface PedidoDeRetomadaData {
  chave: string;
  acao: AcaoDaRetomada;
  justificativa?: string | null;
}

export const JUSTIFICATIVA_MINIMA = 15;

export type MotivoDaFalhaDaConferencia = 'sem_permissao' | 'indisponivel';

export class ConferenciaDaLeituraError extends Error {
  constructor(readonly motivo: MotivoDaFalhaDaConferencia) {
    super(motivo);
    this.name = 'ConferenciaDaLeituraError';
  }
}

/** Por que o pedido da equipe não foi atendido, pelo código do servidor. */
export type RecusaDoPedido =
  | 'sem_retomada'
  | 'nao_vigente'
  | 'sem_falha_tecnica'
  | 'nao_encontrada'
  | 'justificativa'
  | 'indisponivel';

export class PedidoDaEquipeError extends Error {
  constructor(readonly recusa: RecusaDoPedido) {
    super(recusa);
    this.name = 'PedidoDaEquipeError';
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;

const RECUSA_POR_CODIGO: Readonly<Record<string, RecusaDoPedido>> = {
  PENDENCIA_SEM_RETOMADA: 'sem_retomada',
  CONFERENCIA_NAO_VIGENTE: 'nao_vigente',
  PENDENCIA_SEM_FALHA_TECNICA: 'sem_falha_tecnica',
  PENDENCIA_NOT_FOUND: 'nao_encontrada',
  CONFERENCIA_NOT_FOUND: 'nao_encontrada',
};

function detalhe(err: unknown): unknown {
  return axios.isAxiosError(err)
    ? (err.response?.data as { detail?: unknown } | undefined)?.detail
    : undefined;
}

/** A conferência do caso; `null` quando o caso ainda não tem conferência (antes do exame). */
export async function lerConferenciaDaLeitura(
  client: AxiosInstance,
  caseId: string,
): Promise<ConferenciaDaLeituraOutData | null> {
  try {
    const { data } = await client.get<ConferenciaDaLeituraOutData>(
      `/casos/${caseId}/conferencia-da-leitura`,
    );
    return data;
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (status === HTTP_NOT_FOUND && detalhe(err) === 'CONFERENCIA_NOT_FOUND') return null;
    throw new ConferenciaDaLeituraError(
      status === HTTP_FORBIDDEN ? 'sem_permissao' : 'indisponivel',
    );
  }
}

/** O pedido da equipe para uma falha técnica; devolve a conferência refeita. */
export async function pedirPelaEquipe(
  client: AxiosInstance,
  caseId: string,
  pedido: PedidoDeRetomadaData,
): Promise<ConferenciaDaLeituraOutData> {
  try {
    const { data } = await client.post<ConferenciaDaLeituraOutData>(
      `/casos/${caseId}/conferencia-da-leitura/pedidos`,
      pedido,
    );
    return data;
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (status === HTTP_UNPROCESSABLE) throw new PedidoDaEquipeError('justificativa');
    const codigo = detalhe(err);
    const recusa = typeof codigo === 'string' ? RECUSA_POR_CODIGO[codigo] : undefined;
    throw new PedidoDaEquipeError(recusa ?? 'indisponivel');
  }
}

/** A falha técnica aberta: trava o documento emitido até a equipe resolvê-la. */
export function trava(p: PendenciaDaLeituraData): boolean {
  return p.responsavel === 'operacao' && p.limitacao === null;
}

export function falhasTecnicas(c: ConferenciaDaLeituraData): PendenciaDaLeituraData[] {
  return c.pendencias.filter(trava);
}

/** A falha técnica que a equipe registrou como limitação: fica listada e não trava. */
export function limitacoesRegistradas(c: ConferenciaDaLeituraData): PendenciaDaLeituraData[] {
  return c.pendencias.filter((p) => p.responsavel === 'operacao' && p.limitacao !== null);
}

export function pontosDoPerito(c: ConferenciaDaLeituraData): PendenciaDaLeituraData[] {
  return c.pendencias.filter((p) => p.responsavel === 'perito');
}

const DE_PAGINA_RETOMAVEL: ReadonlySet<TipoDaPendencia> = new Set([
  'pagina_por_ler',
  'pagina_com_falha',
  'pagina_sem_texto',
]);

/** A ação principal da falha: ler de novo, quando a página se retoma (o campo essencial segue a
 * página dele e não se lê de novo sozinho); senão, registrar a limitação. */
export function podeTentarDeNovo(p: PendenciaDaLeituraData): boolean {
  return DE_PAGINA_RETOMAVEL.has(p.tipo) && p.retomavel && p.limitacao === null;
}

/** A página do arquivo recebido onde a pendência está, para "ver na fonte". */
export function paginaDaPendencia(p: PendenciaDaLeituraData): PaginaNaFonte | null {
  if (p.original_id === null || p.pagina_no_original === null) return null;
  return {
    documentoId: p.original_id,
    pagina: p.pagina_no_original,
    titulo: p.original_nome ?? p.documento_nome ?? '',
  };
}

export function paginaDoAchado(a: AchadoDoDocumentoData): PaginaNaFonte | null {
  if (a.pagina_no_original === null) return null;
  return { documentoId: a.original_id, pagina: a.pagina_no_original, titulo: a.documento_nome };
}

/** A leitura em recuperação pelo sistema: a tela relê até ela terminar. */
export function emRecuperacao(dados: ConferenciaDaLeituraOutData | null): boolean {
  return dados !== null && dados.conferencia.conferencia.estado === 'corrigir';
}

/** O estado da leitura no caso trava o documento emitido? (`null`: caso sem ficha.) */
export function leituraEmConferencia(estado: EstadoDaLeituraDoCaso | null | undefined): boolean {
  return estado === 'pendente' || estado === 'corrigir' || estado === 'bloqueada';
}
